import { useEffect, useState } from 'react';
import { safeInvoke } from '../lib/safeInvoke';
import type { ServiceDef, ServiceBenefit, ServiceFaq } from '../data/services';

// ─────────────────────────────────────────
// useServiceLandingContent
//
// Fetches AI-generated landing copy (benefits / forWho / faqs /
// previewText) for a given service + language. The edge function
// stores the result in ai_cache forever (TTL 1 year), so the cost is
// paid once per slug+language pair globally.
//
// Returns the static content from services.ts immediately as a
// fallback and swaps in the AI-generated version as soon as it
// arrives. The caller never has to handle a loading state explicitly.
// ─────────────────────────────────────────

interface LandingContent {
  benefits:    ServiceBenefit[];
  forWho:      string[];
  faqs:        ServiceFaq[];
  previewText: string;
}

export function useServiceLandingContent(
  service: ServiceDef,
  language = 'pt-BR',
): LandingContent {
  const [override, setOverride] = useState<LandingContent | null>(null);

  useEffect(() => {
    let active = true;

    safeInvoke<LandingContent>('service-landing-content', {
      body: {
        slug:     service.slug,
        language,
        name:     service.name,
        subtitle: service.subtitle,
      },
      fallback: {
        benefits:    service.benefits,
        forWho:      service.forWho,
        faqs:        service.faqs,
        previewText: service.previewText,
      },
    }).then(({ data: result }) => {
      if (!active) return;
      // Only override if the edge function returned a non-empty payload.
      if (
        result &&
        Array.isArray(result.benefits)   && result.benefits.length   > 0 &&
        Array.isArray(result.forWho)     && result.forWho.length     > 0 &&
        Array.isArray(result.faqs)       && result.faqs.length       > 0 &&
        typeof result.previewText === 'string' && result.previewText.length > 0
      ) {
        setOverride(result);
      }
    }).catch(() => { /* silent, static fallback already rendered */ });

    return () => { active = false; };
  }, [service.slug, language]);

  return override ?? {
    benefits:    service.benefits,
    forWho:      service.forWho,
    faqs:        service.faqs,
    previewText: service.previewText,
  };
}
